import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';   // NAV-1
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { CheckCircle, XCircle, Search, X, Filter, ClipboardList, Clock, CheckCircle2, AlertCircle, Receipt } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

// Status filter pills. Default opens on SUBMITTED — the queue an approver
// usually wants to act on first.
const STATUS_FILTERS = [
  { key: 'SUBMITTED', label: 'Pending' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'INVOICED', label: 'Invoiced' },
  { key: 'all', label: 'All' },
];

export default function TimesheetApproval() {
  const [timesheets, setTimesheets] = useState([]);
  const [searchParams] = useSearchParams();   // NAV-1: dashboard deep link ?status=
  const [statusFilter, setStatusFilter] = useState(() => (STATUS_FILTERS.some((f) => f.key === searchParams.get('status')) ? searchParams.get('status') : 'SUBMITTED'));
  const [search, setSearch] = useState('');
  const [approveConfirm, setApproveConfirm] = useState(null);
  const [rejectConfirm, setRejectConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  const fetchData = async () => {
    setListLoading(true);
    try {
      const url = statusFilter === 'all' ? '/timesheets' : `/timesheets?status_filter=${statusFilter}`;
      const res = await api.get(url);
      setTimesheets(res.data.data || []);
    } catch (e) { toast.error('Failed to load timesheets'); }
    finally { setListLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]); // eslint-disable-line

  const handleApprove = async () => {
    if (!approveConfirm) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/timesheets/${approveConfirm.id}/approve`);
      const ledger = res.data.ledger;
      toast.success(`Approved! Deducted $${ledger?.amount || 0} from ${ledger?.category || 'CORE'}`);
      setApproveConfirm(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectConfirm) return;
    setActionLoading(true);
    try {
      await api.post(`/timesheets/${rejectConfirm.id}/reject`, { reason: 'Rejected by admin' });
      toast.success('Timesheet rejected');
      setRejectConfirm(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(false); }
  };

  const filtered = timesheets.filter(ts => {
    if (!search) return true;
    return ts.staff_name?.toLowerCase().includes(search.toLowerCase()) ||
           ts.participant_name?.toLowerCase().includes(search.toLowerCase()) ||
           ts.date?.includes(search);
  });

  // Read-only aggregates over the currently-loaded page.
  const summary = useMemo(() => {
    const totalHours = timesheets.reduce((s, t) => s + (Number(t.hours_worked) || 0), 0);
    const totalValue = timesheets.reduce((s, t) => s + ((Number(t.hours_worked) || 0) * (Number(t.rate_per_unit) || 0)), 0);
    return { count: timesheets.length, totalHours, totalValue };
  }, [timesheets]);

  const hasActiveFilters = !!search;
  const isEmptySource = !listLoading && timesheets.length === 0;
  const isFilteredEmpty = !listLoading && timesheets.length > 0 && filtered.length === 0;

  const filterIcons = {
    SUBMITTED: AlertCircle,
    APPROVED: CheckCircle2,
    REJECTED: XCircle,
    INVOICED: Receipt,
    all: Filter,
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="timesheet-approval-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Timesheet Approval</h1>
        <p className="text-sm text-muted-foreground mt-1">Review and approve submitted timesheets. Approving deducts from the participant's funding plan.</p>
      </div>

      {/* Summary KPIs (page-scoped, mirrors what the operator can see) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="timesheet-summary">
        <Card className="border-slate-200">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><ClipboardList className="h-3.5 w-3.5" /> Current view</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="timesheet-summary-count">{summary.count}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">timesheet{summary.count === 1 ? '' : 's'}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><Clock className="h-3.5 w-3.5" /> Total hours</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="timesheet-summary-hours">{summary.totalHours.toFixed(1)}h</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total value</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-slate-900" data-testid="timesheet-summary-value">${summary.totalValue.toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card className={`${statusFilter === 'SUBMITTED' ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`}>
          <CardContent className="p-3.5">
            <div className={`flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider ${statusFilter === 'SUBMITTED' ? 'text-amber-700' : 'text-slate-500'}`}><AlertCircle className="h-3.5 w-3.5" /> Awaiting action</div>
            <div className={`mt-1 text-xl font-bold tabular-nums ${statusFilter === 'SUBMITTED' ? 'text-amber-900' : 'text-slate-900'}`} data-testid="timesheet-summary-pending">
              {statusFilter === 'SUBMITTED' ? summary.count : '—'}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">switch to Pending to see queue</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:w-[340px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by staff, participant, date..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="timesheet-search" />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="timesheet-status-filters">
          {/* Hidden legacy Select kept so external automation that targeted
              `data-testid=timesheet-status-filter` keeps working — the visible
              UI is now the pill bar to its right. */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sr-only h-0 w-0 p-0" data-testid="timesheet-status-filter" aria-hidden>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="SUBMITTED">Submitted</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="INVOICED">Invoiced</SelectItem>
            </SelectContent>
          </Select>
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {STATUS_FILTERS.map(s => {
            const Ic = filterIcons[s.key] || Filter;
            return (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                data-testid={`timesheet-status-pill-${s.key.toLowerCase()}`}
                className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${statusFilter === s.key ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <Ic className="h-3 w-3" /> {s.label}
              </button>
            );
          })}
          {hasActiveFilters && (
            <button
              onClick={() => setSearch('')}
              data-testid="timesheet-filters-clear"
              className="shrink-0 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
          <span className="shrink-0 text-xs text-muted-foreground ml-1">{filtered.length} of {timesheets.length}</span>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          {listLoading ? (
            <TableSkeleton rows={6} columns={8} showHeader headers={['Date', 'Staff', 'Participant', 'Service', 'Hours', 'Rate', 'Status', 'Actions']} testid="timesheet-loading" />
          ) : isEmptySource ? (
            <EmptyState
              icon={ClipboardList}
              title={statusFilter === 'SUBMITTED' ? 'No timesheets awaiting approval' : 'No timesheets in this view'}
              description={statusFilter === 'SUBMITTED'
                ? "Looks like you're caught up. Submitted timesheets from staff will appear here for review."
                : 'Try switching the status filter or clearing the search.'}
              testid="timesheet-empty"
              action={statusFilter !== 'all' ? (
                <Button variant="outline" size="sm" onClick={() => setStatusFilter('all')} data-testid="timesheet-empty-show-all">
                  Show all statuses
                </Button>
              ) : null}
            />
          ) : isFilteredEmpty ? (
            <EmptyState
              icon={Search}
              title="No matches"
              description="No timesheets match the current search."
              testid="timesheet-empty-filtered"
              compact
              action={
                <Button variant="outline" size="sm" onClick={() => setSearch('')} data-testid="timesheet-empty-filtered-cta">
                  Clear search
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Participant</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(ts => (
                  <TableRow key={ts.id} className="hover:bg-muted/60" data-testid={`timesheet-row-${ts.id}`}>
                    <TableCell className="font-mono text-xs tabular-nums whitespace-nowrap">{ts.date}</TableCell>
                    <TableCell className="font-medium">{ts.staff_name || 'N/A'}</TableCell>
                    <TableCell>{ts.participant_name || 'N/A'}</TableCell>
                    <TableCell className="text-sm">{ts.service_name || 'N/A'}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{ts.hours_worked}h</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">${ts.rate_per_unit || 0}/h</TableCell>
                    <TableCell><StatusBadge status={ts.status} /></TableCell>
                    <TableCell className="text-right">
                      {ts.status === 'SUBMITTED' ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" onClick={() => setApproveConfirm(ts)} data-testid={`approve-timesheet-${ts.id}`} title="Approve timesheet">
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setRejectConfirm(ts)} data-testid={`reject-timesheet-${ts.id}`} title="Reject timesheet">
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!approveConfirm}
        onOpenChange={() => setApproveConfirm(null)}
        title="Approve Timesheet"
        description={approveConfirm ? `Approve ${approveConfirm.hours_worked}h for ${approveConfirm.staff_name || 'staff'} on ${approveConfirm.date}? This will deduct from the participant's funding plan.` : ''}
        confirmLabel="Approve Timesheet"
        loading={actionLoading}
        onConfirm={handleApprove}
      />

      <ConfirmDialog
        open={!!rejectConfirm}
        onOpenChange={() => setRejectConfirm(null)}
        title="Reject Timesheet"
        description={rejectConfirm ? `Reject timesheet for ${rejectConfirm.staff_name || 'staff'} on ${rejectConfirm.date}?` : ''}
        confirmLabel="Reject"
        variant="destructive"
        loading={actionLoading}
        onConfirm={handleReject}
      />
    </div>
  );
}
